import { effect, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import { ConsoleActions, selectSelectedSubscription, selectSelectedTenant } from '@stores/console';
import { SubscriptionDto, TenantDto, TenantService } from '@stores/tenants';
import { AgentsActions } from '@stores/agents';
import { AiProvidersActions, selectAiProvidersData } from '@stores/ai-providers';
import { AiModelsActions } from '@stores/ai-models';
import { BusinessEntitiesActions } from '@stores/business-entities';
import { PayloadsActions } from '@stores/payloads';

/**
 * Holds the tenant/subscription context driving the AI (console) section:
 * default tenant selection, URL → store synchronisation and the data-load cascade
 * previously owned by the console subscription selector. The sidebar tenant list
 * itself is paginated by the selector (`TenantPagination`).
 */
@Injectable({ providedIn: 'root' })
export class ConsoleContextService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly tenantService = inject(TenantService);

  // First tenants only: used to pick a default and to resolve a tenant from the URL.
  private readonly tenants = signal<readonly TenantDto[]>([]);

  readonly selectedTenant = this.store.selectSignal(selectSelectedTenant);
  readonly selectedSubscription = this.store.selectSignal(selectSelectedSubscription);
  private readonly aiProviders = this.store.selectSignal(selectAiProvidersData);

  readonly isInConsole = signal(this.router.url.startsWith('/console/'));

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => {
        this.isInConsole.set(e.urlAfterRedirects.startsWith('/console/'));
        this.syncFromUrl(e.urlAfterRedirects);
      });
    this.syncFromUrl(this.router.url);
    this.loadTenants();

    effect(() => {
      const sub = this.selectedSubscription();
      if (!sub) return;
      this.store.dispatch(
        AgentsActions.getAgents({
          tenantUuid: sub.tenantUuid,
          subscriptionUuid: sub.subscriptionUuid,
        }),
      );
      this.store.dispatch(
        AiProvidersActions.getAIProviders({
          tenantUuid: sub.tenantUuid,
          subscriptionUuid: sub.subscriptionUuid,
        }),
      );
      this.store.dispatch(
        BusinessEntitiesActions.getBusinessEntities({ tenantUuid: sub.tenantUuid }),
      );
      this.store.dispatch(PayloadsActions.getPayloads({ tenantUuid: sub.tenantUuid }));
      this.store.dispatch(AgentsActions.getAgentHubs({ tenantUuid: sub.tenantUuid }));
    });

    effect(() => {
      const aiProviders = this.aiProviders();
      const sub = this.selectedSubscription();
      if (aiProviders && aiProviders.length > 0 && sub) {
        const aiProviderUuidList = aiProviders.map((aiProvider) => aiProvider.aiProviderUuid);
        this.store.dispatch(
          AiModelsActions.getAIModels({ tenantUuid: sub.tenantUuid, aiProviderUuidList }),
        );
      }
    });
  }

  selectTenant(tenant: TenantDto): void {
    if (this.selectedTenant()?.tenantUuid === tenant.tenantUuid) return;
    const firstSub = tenant.subscriptions?.[0] ?? null;
    this.store.dispatch(ConsoleActions.setSelectedTenant({ tenant }));
    this.store.dispatch(ConsoleActions.setSelectedSubscription({ subscription: firstSub }));
    this.renavigateIfInConsole(tenant.tenantUuid, firstSub?.subscriptionUuid ?? null);
  }

  selectSubscription(subscription: SubscriptionDto): void {
    if (this.selectedSubscription()?.subscriptionUuid === subscription.subscriptionUuid) return;
    this.store.dispatch(ConsoleActions.setSelectedSubscription({ subscription }));
    const tenant = this.selectedTenant();
    if (tenant) {
      this.renavigateIfInConsole(tenant.tenantUuid, subscription.subscriptionUuid);
    }
  }

  private loadTenants(): void {
    this.tenantService
      .getTenantsPage(0, 100, '', true)
      .pipe(take(1))
      .subscribe({
        next: ({ tenants }) => {
          this.tenants.set(tenants);
          this.ensureDefaultSelection();
        },
      });
  }

  private ensureDefaultSelection(): void {
    if (this.selectedTenant() || this.router.url.startsWith('/console/')) return;
    const tenant =
      this.tenants().find((t) => (t.subscriptions ?? []).length > 0) ?? this.tenants()[0] ?? null;
    if (!tenant) return;
    this.store.dispatch(ConsoleActions.setSelectedTenant({ tenant }));
    this.store.dispatch(
      ConsoleActions.setSelectedSubscription({ subscription: tenant.subscriptions?.[0] ?? null }),
    );
  }

  private syncFromUrl(url: string): void {
    const parts = url.split('?')[0].split('/').filter(Boolean);
    if (parts[0] !== 'console' || parts.length < 3) return;
    const [, tenantId, subscriptionId] = parts;
    const currentTenant = this.selectedTenant();
    const currentSub = this.selectedSubscription();
    if (currentTenant?.tenantUuid === tenantId && currentSub?.subscriptionUuid === subscriptionId) {
      return;
    }

    if (currentTenant?.tenantUuid === tenantId) {
      const sub =
        (currentTenant.subscriptions ?? []).find((s) => s.subscriptionUuid === subscriptionId) ??
        null;
      this.store.dispatch(ConsoleActions.setSelectedSubscription({ subscription: sub }));
      return;
    }

    const applyTenant = (tenant: TenantDto) => {
      this.store.dispatch(ConsoleActions.setSelectedTenant({ tenant }));
      const sub =
        (tenant.subscriptions ?? []).find((s) => s.subscriptionUuid === subscriptionId) ?? null;
      this.store.dispatch(ConsoleActions.setSelectedSubscription({ subscription: sub }));
    };

    const known = this.tenants().find((t) => t.tenantUuid === tenantId);
    if (known) {
      applyTenant(known);
      return;
    }
    this.tenantService
      .getTenant(tenantId, true)
      .pipe(take(1))
      .subscribe((tenant) => applyTenant(tenant));
  }

  private renavigateIfInConsole(tenantUuid: string, subscriptionUuid: string | null): void {
    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children['primary']?.segments.map((s) => s.path) ?? [];
    if (segments[0] !== 'console') return;
    if (!subscriptionUuid) {
      void this.router.navigate(['/tenants']);
      return;
    }
    const page = segments[3] ?? 'dashboard';
    void this.router.navigate(['/console', tenantUuid, subscriptionUuid, page], {
      queryParamsHandling: 'preserve',
      fragment: urlTree.fragment ?? undefined,
    });
  }
}
